import { bitmapChannels } from "../bitmap/index.ts";
import { readUInt32BE, writeUInt32BE } from "../utils/uint8-array.ts";
import gaussianBlur from "../bitmap/gaussian-blur.ts";
import sharpen from "../bitmap/sharpen.ts";
import { floatFloor } from "../utils/float.ts";
import compileMaskingProps from "./compile-masking-props.ts";
import type { BuildOperationInput, BuildOperationResult } from "./operation.ts";

// Resembling "unsharp mask" filter from PhotoShop
// Note: Detail filter should not apply to removed background areas. However it
// doesn't impact the flat bg colours
function* detailFilterOperationV2({
	config,
	intermediate: { image },
	sources: { backgroundMask },
}: BuildOperationInput): Generator<undefined, BuildOperationResult> {
	const { detailFilter, removeBackground } = config;
	if (!detailFilter) {
		throw new Error("No detail filter");
	}
	if (!("strength" in detailFilter)) {
		throw new Error(`Invalid detail filter ${JSON.stringify(detailFilter)}`);
	}

	const { strength, radiusRatio } = detailFilter;
	const shortSide = Math.min(image.width, image.height);
	const pixelRadius = Math.max(1, Math.floor(shortSide * radiusRatio));

	const blurred = yield* gaussianBlur(image, pixelRadius);
	yield;
	const sharpened = sharpen(image, blurred, strength);
	yield;

	// We don't want to apply detail filter to background.
	if (removeBackground && backgroundMask) {
		// Background mask is in scale of original source, so need some re-calc.
		const maskingProps = compileMaskingProps(config, backgroundMask, image);
		if (maskingProps) {
			const numPixelChannels = image.width * image.height * bitmapChannels;
			for (let i = 0; i < numPixelChannels; i += bitmapChannels) {
				const pixelIndex = i / bitmapChannels;
				const x = pixelIndex % image.width;
				const y = (pixelIndex - x) / image.width;
				const maskX = floatFloor(
					(x + maskingProps.croppedXOffset) / maskingProps.usedResizeScale,
				);
				const maskY = floatFloor(
					(y + maskingProps.croppedYOffset) / maskingProps.usedResizeScale,
				);

				// If total background, then use previous result. We don't mind if
				// there's some detail handling of the partial opacity mask pixels.
				if (maskingProps.foregroundRatio[maskX][maskY] === 0) {
					writeUInt32BE(sharpened.data, readUInt32BE(image.data, i), i);
					continue;
				}
			}
		}
	}

	return {
		type: "cloned",
		clonedImage: {
			...sharpened,
			noBrickColour: image.noBrickColour,
		},
	};
}

export default detailFilterOperationV2;
